import { View, SearchInput, ActionIcon } from '@/components'
import React, { useCallback, useMemo } from 'react'
import { variantProvider } from '@/app'
import { useFlatlistProps } from '@/utils'
import { useSharedSettings } from '@/services/sharedSettings'
import { SimpleList } from '../SimpleList'

type SearchGlobalProps = {
  onBack: () => void
  listProps: ReturnType<typeof useFlatlistProps>
  renderCard: (data: any) => React.JSX.Element
  onSearch: (value: string) => void
  search: string
}

export const SearchGlobal = ({ onBack, listProps, renderCard, onSearch, search }: SearchGlobalProps) => {
  const sharedSettings = useSharedSettings()
  const learnTexts = sharedSettings?.settings?.learn

  const handleSearch = useCallback((value: string) => {
    onSearch(value)
  }, [])

  const handleGoBack = useCallback(() => {
    onSearch('')
    onBack()
  }, [])

  const placeholder = useMemo(() => {
    return !!search
      ? {
          title: learnTexts?.placeholder,
          description: learnTexts?.notFoundDescription
        }
      : {
          title: learnTexts?.emptyPlaceholderTitle,
          description: learnTexts?.emptyPlaceholderDescription
        }
  }, [search])

  return (
    <View style={styles.wrapper} variants={['column', 'paddingHorizontal:1', 'paddingBottom:1']}>
      <View variants={['row', 'center', 'gap:1']} responsiveVariants={{ [BREAKPOINT]: ['row', 'gap:2', 'fullWidth'] }}>
        <ActionIcon
          debugName="hurdle back"
          icon="chevron-left"
          variants={['normalize', 'neutral10', 'iconSize:4']}
          responsiveVariants={{ [BREAKPOINT]: ['iconSize:5'] }}
          onPress={handleGoBack}
        />

        <SearchInput
          placeholder={'Search'}
          debugName={'Search Feed Input'}
          variants={['marginTop:2', 'marginBottom:2', 'noError']}
          debounce={1000}
          value={search}
          onSearchChange={handleSearch}
          autoFocus
        />
      </View>

      <SimpleList<any>
        {...listProps}
        renderItem={renderCard}
        keyExtractor={(item, index) => item?.id}
        style={styles.list}
        placeholder={placeholder}
      />
    </View>
  )
}

const BREAKPOINT = 'small'

const styles = variantProvider.createComponentStyle(
  theme => ({
    wrapper: {
      maxWidth: 600,
      width: '100vw',

      [theme.media.down(BREAKPOINT)]: {
        width: '100%',
        maxHeight: '100%'
      }
    },
    title: {
      color: theme.colors.neutral9
    },
    description: {
      color: theme.colors.neutral5
    },
    list: {
      ...theme.presets.column,
      ...theme.spacing.gap(2),
      height: '60dvh',
      overflowY: 'auto',
      overflowX: 'hidden',

      [theme.media.down(BREAKPOINT)]: {
        height: '75dvh'
      }
    }
  }),
  true
)
